import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, patch, del } from '../lib/apiClient';

export interface EmailProviderConfig {
  id: string;
  provider: 'gmail' | 'microsoft365' | 'custom';
  label: string | null;
  first_name: string | null;
  last_name: string | null;
  signature: string | null;
  reply_to_address: string | null;
  tags: string[];
  smtp_host: string;
  smtp_port: number;
  encryption: 'tls' | 'ssl' | 'starttls' | 'none';
  smtp_username: string;
  sender_email: string | null;
  smtp_password: string | null;
  password_set: boolean;
  is_default: boolean;
  is_active: boolean;
  can_send: boolean | null;
  can_receive: boolean | null;
  created_at: string | null;
  oauth_connected: boolean;
  oauth_email: string | null;
  oauth_token_expires_at: string | null;
  imap_host: string | null;
  imap_port: number | null;
  imap_encryption: string | null;
  sender_profile_id: string | null;
  daily_campaign_limit: number;
  min_wait_time_minutes: number;
  campaign_slow_ramp: boolean;
  daily_inbox_placement_limit: number;
  custom_tracking_domain_enabled: boolean;
  custom_tracking_domain: string | null;
  warmup_enabled: boolean;
  warmup_limit: number;
  warmup_increment: number;
  emails_sent_today: number;
  emails_scheduled_today: number;
  effective_daily_limit: number;
}

export interface EmailProviderPayload {
  provider: 'gmail' | 'microsoft365' | 'custom';
  label?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  signature?: string | null;
  reply_to_address?: string | null;
  tags?: string[];
  smtp_host: string;
  smtp_port: number;
  encryption: 'tls' | 'ssl' | 'starttls' | 'none';
  smtp_username: string;
  sender_email?: string | null;
  smtp_password: string;
  imap_host?: string | null;
  imap_port?: number | null;
  imap_encryption?: string | null;
  tc_accepted: true;
  is_default?: boolean;
  is_active?: boolean;
  daily_campaign_limit?: number;
  min_wait_time_minutes?: number;
  campaign_slow_ramp?: boolean;
  daily_inbox_placement_limit?: number;
  custom_tracking_domain_enabled?: boolean;
  custom_tracking_domain?: string | null;
  warmup_enabled?: boolean;
  warmup_limit?: number;
  warmup_increment?: number;
}

export function useEmailProvidersQuery(enabled = true) {
  return useQuery({
    queryKey: ['email-providers'],
    queryFn: () => get<EmailProviderConfig[]>('settings/email-providers'),
    enabled,
  });
}

export function useCreateEmailProviderMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EmailProviderPayload) =>
      post<EmailProviderConfig>('settings/email-providers', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-providers'] });
    },
  });
}

export function useUpdateEmailProviderMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<EmailProviderPayload> }) =>
      patch<EmailProviderConfig>(`settings/email-providers/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-providers'] });
    },
  });
}

export function useDeleteEmailProviderMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`settings/email-providers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-providers'] });
    },
  });
}

export function useTestEmailProviderMutation() {
  return useMutation({
    mutationFn: ({ id, to }: { id: string; to: string }) =>
      post<{ message: string }>(`settings/email-providers/${id}/test`, { to }),
  });
}

export function useBulkImportEmailProvidersMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return post<{ created: number; failed: { row: number; email: string; error: string }[] }>(
        'settings/email-providers/bulk-import',
        form,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-providers'] });
    },
  });
}

export interface EmailProviderSendStats {
  daily: { date: string; day: string; sent: number }[];
  total_sent: number;
}

export function useEmailProviderSendStats(providerId: string | null) {
  return useQuery({
    queryKey: ['email-provider-send-stats', providerId],
    queryFn: () => get<EmailProviderSendStats>(`settings/email-providers/${providerId}/send-stats`),
    enabled: !!providerId,
    staleTime: 60_000,
  });
}

export interface EmailProviderCampaign {
  id: string;
  name: string;
  status: 'active' | 'paused' | 'completed' | 'draft' | 'deleted';
}

export function useEmailProviderCampaigns(providerId: string | null) {
  return useQuery({
    queryKey: ['email-provider-campaigns', providerId],
    queryFn: () => get<EmailProviderCampaign[]>(`settings/email-providers/${providerId}/campaigns`),
    enabled: !!providerId,
    staleTime: 60_000,
  });
}

export function useDisconnectEmailProviderMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (provider: 'gmail' | 'microsoft365') => {
      const providerSlug = provider === 'gmail' ? 'google' : 'microsoft';
      return del(`settings/email-providers/oauth/${providerSlug}/disconnect`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-providers'] });
      queryClient.invalidateQueries({ queryKey: ['sender-identities'] });
    },
  });
}
